import React, { useState, useEffect, useCallback, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DiscountModal from '@wetransfer/transfer/component/DiscountModal/DiscountModal';
import {
  FF_BUNDLE_SPLASHPAGE_EXPERIMENT,
  FF_RECIPIENT_SPLASH_PAGE,
} from '@wetransfer/transfer/constant/experiments';
import ExperimentDiscountModal from '@wetransfer/transfer/constant/experiments/discount-modal';
import ExperimentSplashCtaTextExperiment from '@wetransfer/transfer/constant/experiments/splash-cta-text-experiment';
import { PLAN_TIER } from '@wetransfer/transfer/constant/plan-tier';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import showPricingPageHelper from '@wetransfer/transfer/helper/showPricingPageToFAExperiment';
import SplashpageHelper from '@wetransfer/transfer/helper/splashpage';
import { userHasPaidTier } from '@wetransfer/transfer/helper/user';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import { getVariant } from '@wetransfer/transfer/modules/experiments';
import I18n from '@wetransfer/transfer/modules/i18n';
import Store, { initialState } from '@wetransfer/transfer/store';
import Actions from '@wetransfer/transfer/store/action';
import { selectUser } from '@wetransfer/transfer/store/selector/user';
import { State } from '@wetransfer/transfer/store/types';

import {
  default as SplashPageComponent,
  SplashPageContent,
} from './content/Content';
import * as variants from './variants';

interface Props {
  onVisible: (isOpen: boolean) => void;
  onMount: () => void;
}

export const SplashPage = ({ onVisible, onMount }: Props): JSX.Element => {
  const { route, user } = Store.getState();
  const currentUser = useSelector(selectUser);

  const { domain } = useSelector((state: State) => ({
    domain: state.session.domain,
  }));
  const showModalExperiment = ExperimentDiscountModal.isShowDiscountModal();

  const shouldShowReceiverSplashPage =
    getVariant(FF_RECIPIENT_SPLASH_PAGE) === 'on' &&
    route.path.includes('/downloads/');

  const [visible, setVisible] = useState(
    SplashpageHelper.shouldDisplaySplashpage()
  );

  const [splashPageWasVisible, setSplashPageWasVisible] = useState(false);

  const dispatch = useDispatch();

  const { countryCode } = useSelector(({ session }: typeof initialState) => ({
    countryCode: session.region.country,
  }));

  const handleDismiss = useCallback(() => {
    closeSplashpage();
  }, []);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      handleDismiss();
    },
    [handleDismiss]
  );

  const trackingLabel = shouldShowReceiverSplashPage ? 'receiving' : 'sending';
  const isWallpaperURL = window.location.pathname.includes('/wallpaper/');
  const modalHasBeenSeen = Cookie.get(AppConfig.cookies.discountModalSeen.name);

  const search = window.location.search;
  const isWallpaperQuery = new URLSearchParams(search).get('ad_id');

  const showDiscountModal =
    showModalExperiment &&
    !modalHasBeenSeen &&
    !shouldShowReceiverSplashPage &&
    !Browser.isMobile &&
    !userHasPaidTier(user) &&
    !isWallpaperURL &&
    !isWallpaperQuery;

  useEffect(() => {
    if (visible) {
      SplashpageHelper.trackFunnel(
        'show_page',
        'WeTransfer_Site_Landing_Page_Overlay_Visit',
        trackingLabel
      );
      setSplashPageWasVisible(true);
      onVisible(true);
    } else {
      onVisible(false);

      if (
        showPricingPageHelper.isPricingPageExperimentVisible(currentUser) &&
        !splashPageWasVisible
      ) {
        showPricingPageHelper.showPricingPageToFA(domain);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (visible) {
      window.addEventListener('keydown', handleEscape);
    }

    return () => window.removeEventListener('keydown', handleEscape);
  }, [handleEscape, visible]);

  useEffect(() => {
    if (visible) {
      if (countryCode) {
        dispatch(
          Actions.UI.getTiersPricing({
            country_code: countryCode,
            planTier: PLAN_TIER.PLUS,
          })
        );
      }
    }
  }, [countryCode, dispatch, visible]);

  function closeSplashpage() {
    setVisible(false);
    SplashpageHelper.writeCookie();
  }

  useEffect(() => {
    if (!showModalExperiment && visible) {
      trackAmplitudeEvent({
        action: 'Upsell',
        additionalProperties: {
          Context: 'Send: Splash Page',
          Type: 'Splash Page',
          Action: 'Shown',
        },
      });
    }
  }, [showModalExperiment]);

  function navigateToFreeSignUp(e: MouseEvent) {
    e.stopPropagation();
    SplashpageHelper.trackFunnel('click_sign_up_for_free', null, trackingLabel);

    trackAmplitudeEvent({
      action: 'Upsell',
      additionalProperties: {
        Context: 'Send: Splash Page',
        Type: 'Splash Page',
        Action: 'Sign Up',
      },
    });
    closeSplashpage();
  }

  function navigateToProSignUp(e: MouseEvent) {
    e.stopPropagation();

    trackAmplitudeEvent({
      action: 'Upsell',
      additionalProperties: {
        Context: 'Send: Splash Page',
        Type: 'Splash Page',
        Action: 'Upgrade',
        'Subscription Tier': 'Pro',
      },
    });

    if (ExperimentSplashCtaTextExperiment.isEnabled()) {
      Analytics.trackSnowplowEvent({
        category: 'splashscreen',
        action: 'click_learn_about_plan',
        property: 'pro_2022',
        label: 'sending',
      });

      Analytics.trackGoogleEvent({
        category: 'splashscreen',
        action: 'click_learn_about_plan',
        property: 'pro_2022',
        label: 'sending',
      });
    } else {
      Analytics.trackSnowplowEvent({
        category: 'splashscreen',
        action: 'click_get_wetransfer_plus',
        property: 'pro_2022',
        label: 'sending',
      });

      Analytics.trackGoogleEvent({
        category: 'splashscreen',
        action: 'click_get_wetransfer_plus',
        property: 'pro_2022',
        label: 'sending',
      });
    }
    SplashpageHelper.trackFunnel(
      'click_get_wetransfer_plus',
      false,
      trackingLabel
    );
    closeSplashpage();
  }

  function navigateToPricingPage(e: MouseEvent) {
    e.stopPropagation();
    SplashpageHelper.trackFunnel('click_learn_about_plan', null, trackingLabel);
    closeSplashpage();
  }

  function navigateToPremiumSignUp(e: MouseEvent) {
    e.stopPropagation();

    trackAmplitudeEvent({
      action: 'Upsell',
      additionalProperties: {
        Context: 'Send: Splash Page',
        Type: 'Splash Page',
        Action: 'Upgrade',
        'Subscription Tier': 'Premium',
      },
    });

    if (ExperimentSplashCtaTextExperiment.isEnabled()) {
      Analytics.trackSnowplowEvent({
        category: 'splashscreen',
        action: 'click_learn_about_plan',
        property: 'premium_2022',
        label: 'sending',
      });

      Analytics.trackGoogleEvent({
        category: 'splashscreen',
        action: 'click_learn_about_plan',
        property: 'premium_2022',
        label: 'sending',
      });
    } else {
      Analytics.trackSnowplowEvent({
        category: 'splashscreen',
        action: 'click_get_wetransfer_plus',
        property: 'premium_2022',
        label: 'sending',
      });

      Analytics.trackGoogleEvent({
        category: 'splashscreen',
        action: 'click_get_wetransfer_plus',
        property: 'premium_2022',
        label: 'sending',
      });
    }
    closeSplashpage();
  }

  const variant =
    getVariant(FF_BUNDLE_SPLASHPAGE_EXPERIMENT, 'on') === 'on'
      ? 'c'
      : 'control';

  const controlProps = variants.controlProps({
    freeFeatures: variants.freeFeatures(),
    proFeatures: variants.proFeatures(),
    premiumFeatures: variants.premiumFeatures(),
  });

  const availableVariants = {
    control: variants.controlProps({
      freeFeatures: variants.freeFeatures(),
      proFeatures: variants.proFeatures(),
      premiumFeatures: variants.premiumFeatures(),
    }),
    c: variants.variantCProps({
      freeContent: controlProps.freeContent,
      proContent: controlProps.proContent,
      premiumContent: controlProps.premiumContent,
      bundleFreeFeatures: variants.bundleFreeFeatures(),
      bundleProFeatures: variants.bundleProFeatures(),
      bundlePremiumFeatures: variants.bundlePremiumFeatures(),
    }),
  };

  const receiverVariant = variants.receiverProps({
    freeFeatures: variants.receiverFreeFeatures(),
    proFeatures: variants.receiverProFeatures(),
  });

  const receiverProps = {
    proContent: {
      ...receiverVariant.proContent,
      cta: {
        ...receiverVariant.proContent.cta,
        onClick: navigateToPricingPage,
      },
    },
    freeContent: {
      ...receiverVariant.freeContent,
      ...(receiverVariant.freeContent.cta && {
        cta: {
          ...receiverVariant.freeContent.cta,
          onClick: navigateToFreeSignUp,
        },
      }),
    },
  };

  const getSplashPageVariant = () => {
    const {
      proContent: { cta: proCta, ...proProps },
      freeContent: { cta: freeCta, ...freeProps },
      premiumContent: { cta: premiumCta, ...premiumProps },
    } = availableVariants[variant];

    const props = {
      proContent: {
        ...proProps,
        cta: { ...proCta, onClick: navigateToProSignUp },
      },
      freeContent: {
        ...freeProps,
        ...(freeCta && {
          cta: { ...freeCta, onClick: navigateToFreeSignUp },
        }),
      },
      premiumContent: {
        ...premiumProps,
        ...(premiumCta && {
          cta: { ...premiumCta, onClick: navigateToPremiumSignUp },
        }),
      },
    };

    if (shouldShowReceiverSplashPage) {
      return <SplashPageContent {...receiverProps} />;
    }

    return <SplashPageContent {...props} />;
  };

  const { title } = availableVariants[variant];

  const getTitle = () => {
    if (shouldShowReceiverSplashPage) {
      return I18n.t('page.splashpage.reciever.title');
    } else if (title) {
      return title;
    }

    return I18n.t('splash.heading.free_account');
  };

  function closeModal() {
    setVisible(false);
    Cookie.set(AppConfig.cookies.discountModalSeen.name, true);
  }

  //TODO: This logic can be revisited, done this way due to time and not preffered approach
  if (showDiscountModal) {
    return (
      <DiscountModal
        onDismissModal={closeModal}
        payload={ExperimentDiscountModal.payload}
      />
    );
  }

  return (
    <SplashPageComponent
      onDismiss={handleDismiss}
      visible={visible}
      className={`splashpage--variant-${variant}`}
      title={getTitle()}
    >
      {getSplashPageVariant()}
    </SplashPageComponent>
  );
};

export default SplashPage;
